import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  einvoiceApi,
  einvoiceQueryKeys,
  type EInvoiceVatRate,
} from "../../shared/api/einvoice";
import { apiError } from "../../shared/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select as UiSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const VAT_RATES: [EInvoiceVatRate, string][] = [
  ["TAX_EXEMPT", "Không chịu thuế (khám chữa bệnh)"],
  ["NOT_DECLARED", "Không kê khai"],
  ["ZERO", "0%"],
  ["FIVE", "5%"],
  ["EIGHT", "8%"],
  ["TEN", "10%"],
];

/** Hóa đơn điện tử (PA Vietnam) — cấu hình đơn vị phát hành + chế độ phát hành. */
export function EInvoiceSettingsPage() {
  const qc = useQueryClient();
  const bindingQ = useQuery({
    queryKey: einvoiceQueryKeys.binding(),
    queryFn: () => einvoiceApi.getBinding(),
  });

  const [baseUrl, setBaseUrl] = useState("");
  const [environment, setEnvironment] = useState("PRODUCTION");
  const [serial, setSerial] = useState("");
  const [sellerTaxCode, setSellerTaxCode] = useState("");
  const [defaultVatRate, setDefaultVatRate] = useState<EInvoiceVatRate>("TAX_EXEMPT");
  const [autoIssue, setAutoIssue] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const b = bindingQ.data;
    if (!b) return;
    setBaseUrl(b.baseUrl ?? "");
    setEnvironment(b.environment);
    setSerial(b.serial ?? "");
    setSellerTaxCode(b.sellerTaxCode ?? "");
    setDefaultVatRate(b.defaultVatRate);
    setAutoIssue(b.autoIssue);
    setEnabled(b.enabled);
    setCreds({});
  }, [bindingQ.data]);

  const save = useMutation({
    mutationFn: () =>
      einvoiceApi.updateBinding({
        provider: "PA_VIETNAM",
        baseUrl: baseUrl || null,
        environment,
        serial: serial || null,
        sellerTaxCode: sellerTaxCode || null,
        defaultVatRate,
        autoIssue,
        enabled,
        credentials: Object.keys(creds).length ? creds : undefined,
      }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: einvoiceQueryKeys.binding() });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const hasCreds = !!bindingQ.data?.hasCredentials;

  return (
    <div className="grid max-w-3xl gap-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Hóa đơn điện tử</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Phát hành hóa đơn điện tử qua PA Vietnam (hoadon30s). Dịch vụ khám chữa bệnh thường
          <strong> không chịu thuế GTGT</strong> — chọn thuế suất phù hợp. Hóa đơn chỉ đẩy về Cơ quan
          Thuế; dữ liệu y tế (đơn thuốc, bệnh án) đi kênh riêng.
        </p>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6">
          <div className="grid gap-1.5">
            <Label htmlFor="ei-baseurl">Base URL PA Vietnam</Label>
            <Input
              id="ei-baseurl"
              value={baseUrl}
              placeholder="https://hoadon30s.vn"
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ei-env">Môi trường</Label>
            <UiSelect value={environment} onValueChange={setEnvironment}>
              <SelectTrigger id="ei-env">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PRODUCTION">PRODUCTION</SelectItem>
                <SelectItem value="SANDBOX">SANDBOX</SelectItem>
              </SelectContent>
            </UiSelect>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ei-clientid">
              Client ID{hasCreds ? " (đã lưu — để trống nếu giữ nguyên)" : ""}
            </Label>
            <Input
              id="ei-clientid"
              autoComplete="off"
              value={creds.clientId ?? ""}
              onChange={(e) => setCreds((c) => ({ ...c, clientId: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ei-secret">
              Client Secret{hasCreds ? " (đã lưu — để trống nếu giữ nguyên)" : ""}
            </Label>
            <Input
              id="ei-secret"
              type="password"
              autoComplete="new-password"
              placeholder={hasCreds ? "••••••" : ""}
              value={creds.clientSecret ?? ""}
              onChange={(e) => setCreds((c) => ({ ...c, clientSecret: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ei-serial">Ký hiệu hóa đơn (serial)</Label>
            <Input
              id="ei-serial"
              value={serial}
              placeholder="1C25TPA"
              onChange={(e) => setSerial(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ei-mst">Mã số thuế người bán (phòng khám)</Label>
            <Input
              id="ei-mst"
              value={sellerTaxCode}
              onChange={(e) => setSellerTaxCode(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Thuế suất mặc định</Label>
            <UiSelect
              value={defaultVatRate}
              onValueChange={(v) => setDefaultVatRate(v as EInvoiceVatRate)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VAT_RATES.map(([v, l]) => (
                  <SelectItem key={v} value={v}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </UiSelect>
          </div>

          <label className="flex items-center gap-2">
            <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(v === true)} />
            <span className="text-sm">Bật phát hành hóa đơn điện tử cho phòng khám</span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={autoIssue}
              disabled={!enabled}
              onCheckedChange={(v) => setAutoIssue(v === true)}
            />
            <span className="text-sm">
              Tự động phát hành khi hóa đơn thanh toán đủ (nếu tắt: nhân viên bấm phát hành từng hóa
              đơn)
            </span>
          </label>

          {error && <p className="text-sm text-destructive">{error}</p>}
          <div>
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Đang lưu…" : "Lưu cấu hình"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
